import React, { PropTypes } from 'react';
import { identity, inc, ifElse } from 'ramda';
import { Route, Redirect, Switch } from 'react-router-dom';
import moment from 'moment';
import RouteWrapper from '../../RouteWrapper';
import Homepage from '../../Homepage/container';
import SubscriptionMarketing from '../../Account/components/Subscription/Marketing/container';
import FlightTrackerSearch from '../../FlightTracker/components/Search/FlightTrackerSearch';
import MultiFlight from '../../MultiFlight/container';
import FlightTrackerContainer from '../../SingleFlight/container';
import ExtendedDetails from '../../FlightTracker/components/ExtendedDetails/container';
import MapAttributions from '../../../src/containers/TermsAndCredits/MapAttributions';
import EnterpriseAdminConsole from '../../../src/components/enterpriseAdminConsole/container';
import FourOhFour from '../../../src/components/lib/FourOhFour';
import MyAccount from '../../Account/components/MyAccount/MyAccount';
import AccountPanel from '../../Account/components/AccountPanel';
import CurrentConditions from '../../AirportCurrentConditions/components/CurrentConditionsContainer';
import AirportSearch from '../../AirportCurrentConditions/components/AirportSearch/AirportSearch';
import HistoricalFlight from '../../HistoricalFlightStatus/components/routes';
import DataExportsHistoryContainer from '../../DataExports/components/DataExportsHistoryDetail/DataExportsHistoryContainer';
import ExportsSearchContainer from '../../DataExports/components/ExportsSearch/ExportsSearchContainer';
import NewDataExportsContainer from '../../DataExports/components/NewDataExports/NewDataExportsContainer';
import DataExportsHomeContainer from '../../DataExports/components/home/DataExportsHomeContainer';
import DataExportsPurchaseData from '../../DataExports/components/PurchaseData/DataExportsPurchaseDataContainer';
import Subscribe from '../../Account/components/Subscription/Subscribe';
import Login from '../../Account/components/Login';
import SubscriptionSuccess from '../../Account/components/Subscription/Success/container';
import AccountRegistrationContainer from '../../Account/components/AccountRegistration/AccountRegistrationContainer';
import ViewPaymentHistoryDetail from '../../Account/components/ViewPaymentHistoryDetail/ViewPaymentHistoryDetail';
import DeparturesArrivals from '../../AirportDeparturesArrivals/components/DeparturesArrivalsContainer';
import DeparturesArrivalsLanding from '../../AirportDeparturesArrivals/components/Landing/DeparturesArrivalsLanding';
import DelayMap from '../../AirportDelayMap/components/container';
import FlightAlertsIndex from '../../FlightAlerts/components/FlightAlertsIndex/container';
import FlightAlertHistory from '../../FlightAlerts/components/FlightAlertsHistory/container';
import FlightAlertHistoryDetails from '../../FlightAlerts/components/FlightAlertsHistory/FlightAlertHistoryDetails/container';
import MonthlyPerformanceReports from '../../MonthlyPerformanceReports/components/container';
import GlobalCancellationsDelays from '../../GlobalCancellationsDelays/components/container';
import { SegmentSelect, NewFlightAlert } from '../../FlightAlerts/components/FlightAlertWizard/index';
import OnTimePerformance from '../../OnTimePerformance/components/container';

const OnEnterFlightAlerts = ['requireActiveSubscription', 'requireLogin', 'requireVerifiedEmail'];
const OnEnterDataExports = ['requireLogin', 'requireVerifiedEmail'];
const formatDayParams = ifElse(
  day => day < 10,
  day => `0${day}`,
  day => day,
);
const dateFormatters = {
  year: identity, // simply return the year
  month: inc, // add one to the resulting month
  date: d => formatDayParams(d), // add '0' to single digit dates
};
const formatDateParams = () => {
  const now = moment();
  const funcs = Object.keys(dateFormatters);
  return funcs
    .map(a => now[a]()) // now.year(), now.month....
    .map((b, index) => dateFormatters[funcs[index]](b));
};

const Status = ({ code, children }) => (
  <Route render={({ staticContext }) => {
    if (staticContext) {
      staticContext.status = code; // eslint-disable-line no-param-reassign
    }
    return children;
  }}
  />
);

Status.propTypes = {
  code: PropTypes.number,
  children: PropTypes.object,
};

const NotFound = () => (
  <Status code={404}>
    <FourOhFour />
  </Status>
);

export default () => (
  <Switch>
    <RouteWrapper
      path='/'
      component={Homepage}
      name='Homepage'
      exact
    />
    <Route
      path='/flight-tracker/search'
      component={FlightTrackerSearch}
      allowSitemap
    />
    <Route
      path='/flight-tracker/departing/:departureAirportCode'
      exact
      allowSitemap
      render={(renderProps) => {
        const { params } = renderProps.match;
        const [year, month, date] = formatDateParams();
        return (
          <Redirect
            {...renderProps}
            to={`/flight-tracker/departing/${params.departureAirportCode}/${year}/${month}/${date}/6`}
          />
        );
      }}
    />
    <RouteWrapper
      path='/flight-tracker/departing/:departureAirportCode/:year?/:month?/:day?/:hour?/:carrier?'
      component={MultiFlight}
      name='MultiFlight'
    />
    <Route
      path='/flight-tracker/arriving/:arrivalAirportCode'
      exact
      allowSitemap
      render={(renderProps) => {
        const { params } = renderProps.match;
        const [year, month, date] = formatDateParams();
        return (
          <Redirect
            {...renderProps}
            to={`/flight-tracker/arriving/${params.arrivalAirportCode}/${year}/${month}/${date}/6`}
          />
        );
      }}
    />
    <RouteWrapper
      path='/flight-tracker/arriving/:arrivalAirportCode/:year?/:month?/:day?/:hour?/:carrier?'
      component={MultiFlight}
      name='MultiFlight'
    />
    <Route
      path='/flight-tracker/route/:departureAirportCode/:arrivalAirportCode'
      exact
      allowSitemap
      render={(renderProps) => {
        const { params } = renderProps.match;
        const [year, month, date] = formatDateParams();
        return (
          <Redirect
            {...renderProps}
            to={`/flight-tracker/route/${params.departureAirportCode}/${params.arrivalAirportCode}/${year}/${month}/${date}/6`}
          />
        );
      }}
    />
    <RouteWrapper
      path='/flight-tracker/route/:departureAirportCode/:arrivalAirportCode/:year?/:month?/:day?/:hour?/:carrier?'
      component={MultiFlight}
      name='MultiFlight'
    />
    <RouteWrapper
      onEnter={['validateTrackerDate']}
      path='/flight-tracker/:carrier/:flightNumber/:year/:month/:day/:flightId/map'
      component={FlightTrackerContainer}
      name='FlightTracker'
    />
    <RouteWrapper
      onEnter={['validateTrackerDate']}
      path='/flight-tracker/:carrier/:flightNumber/:year/:month/:day/:flightId?'
      component={FlightTrackerContainer}
      name='FlightTracker'
    />
    <RouteWrapper
      path='/flight-tracker/:carrier/:flightNumber/:flightId/map'
      component={FlightTrackerContainer}
      name='FlightTracker'
    />
    <RouteWrapper
      path='/flight-tracker/:carrier/:flightNumber/:flightId'
      component={FlightTrackerContainer}
      name='FlightTracker'
    />
    <Route
      path='/flight-tracker/:carrier/:flightNumber'
      exact
      allowSitemap
      render={(renderProps) => {
        const { params } = renderProps.match;
        const [year, month, date] = formatDateParams();
        return (
          <Redirect
            {...renderProps}
            to={`/flight-tracker/${params.carrier}/${params.flightNumber}/${year}/${month}/${date}`}
          />
        );
      }}
    />
    <Redirect
      from='/flight-tracker/'
      to='/flight-tracker/search'
    />
    <RouteWrapper
      path='/airports/departing-arriving/search'
      component={DeparturesArrivalsLanding}
      onEnter={['requireActiveSubscription', 'requireVerifiedEmail']}
      name='DeparturesArrivalsLanding'
      exact
    />
    <RouteWrapper
      path='/airports/departing-arriving/:airportCode/:departureArrival/:year/:month/:day'
      component={DeparturesArrivals}
      onEnter={['requireActiveSubscription', 'requireVerifiedEmail']}
      name='DeparturesArrivals'
    />
    <Route
      path='/airports/departing-arriving/:airportCode/:departureArrival'
      render={(renderProps) => {
        const { params } = renderProps.match;
        const [year, month, date] = formatDateParams();
        return (
          <Redirect
            {...renderProps}
            to={`/airports/departing-arriving/${params.airportCode}/${params.departureArrival}/${year}/${month}/${date}`}
          />
        );
      }}
    />
    <RouteWrapper
      path='/airports/departing-arriving/subscribe'
      name='SubscriptionMarketing'
      component={SubscriptionMarketing}
      subscriptionType='DEPARR'
      exact
    />
    <Route
      path='/airports/departing-arriving'
      exact
      disallowRobots
      render={() => (
        <Redirect
          to='/airports/departing-arriving/search'
        />
      )}
    />
    <RouteWrapper
      path='/subscription'
      component={SubscriptionMarketing}
      name='SubscriptionMarketing'
      exact
      allowSitemap
    />
    <RouteWrapper
      path='/account/subscribe'
      component={Subscribe}
      name='Subscribe'
      onEnter={['requireSignUp']}
    />
    <RouteWrapper
      path='/account/success'
      name='SubscriptionSuccess'
      component={SubscriptionSuccess}
      onEnter={['requireLogin']}
    />
    <RouteWrapper
      path='/account/invoice/:referenceNumber'
      component={ViewPaymentHistoryDetail}
      name='ViewPaymentHistoryDetail'
      onEnter={['requireLogin']}
    />
    <RouteWrapper
      // Don't name this route
      path='/my-account'
      component={MyAccount}
      onEnter={['requireLogin']}
      disallowRobots
    />
    <RouteWrapper
      path='/account/login'
      name='Login'
      component={Login}
    />
    <RouteWrapper
      // Don't name this route
      path='/account'
      component={AccountPanel}
      disallowRobots
      // this is the last /account route for a reason
      // catchall redirect contained in AccountPanel
    />
    <Route
      path='/register'
      key='registration'
      component={AccountRegistrationContainer}
      disallowRobots
    />
    <RouteWrapper
      path='/map-attributions'
      component={MapAttributions}
      name='TermsAndCreditsMapAttributions'
      allowSitemap
    />
    <RouteWrapper
      path='/enterprise-control-panel'
      component={EnterpriseAdminConsole}
      onEnter={['requireLogin']}
      name='EnterpriseAdminConsole'
      disallowRobots
    />
    <RouteWrapper
      path='/airport-conditions/search'
      component={AirportSearch}
      name='AirportSearch'
      allowSitemap
    />
    <RouteWrapper
      path='/airport-conditions/:airportCode'
      component={CurrentConditions}
      name='AirportCurrentConditions'
      allowSitemap
    />
    <Route
      exact
      path='/airport-conditions'
      render={() => (
        <Redirect
          to='/airport-conditions/search'
        />
      )}
    />
    <RouteWrapper
      path='/data-exports/home'
      component={DataExportsHomeContainer}
      onEnter={OnEnterDataExports}
      name='DataExports'
    />
    <RouteWrapper
      path='/data-exports/new'
      component={NewDataExportsContainer}
      onEnter={OnEnterDataExports}
      name='NewDataExports'
    />
    <RouteWrapper
      path='/data-exports/search/:exportType'
      component={ExportsSearchContainer}
      onEnter={OnEnterDataExports}
      name='ExportsSearch'
    />
    <RouteWrapper
      path='/data-exports/history/:id'
      component={DataExportsHistoryContainer}
      onEnter={OnEnterDataExports}
      name='DataExportsHistoryDetail'
    />
    <RouteWrapper
      path='/data-exports/purchase'
      component={DataExportsPurchaseData}
      onEnter={OnEnterDataExports}
      name='PurchaseData'
    />
    <Route
      disallowRobots
      exact
      path='/data-exports'
      render={() => (
        <Redirect
          to='/data-exports/home'
        />
      )}
    />
    <RouteWrapper
      path='/flight-alerts/subscribe'
      name='FlightAlertsSubscriptionMarketing'
      component={SubscriptionMarketing}
      subscriptionType='ALERTS'
      allowSitemap
      allowRobots
      exact
    />
    <RouteWrapper
      path='/historical-flight/subscribe'
      name='HistoricalSubscriptionMarketing'
      component={SubscriptionMarketing}
      subscriptionType='HISTORICAL'
      allowSitemap
      allowRobots
      exact
    />
    <Route
      path='/historical-flight'
      component={HistoricalFlight}
      disallowRobots
    />
    <RouteWrapper
      path='/airports/departing-arriving/subscribe'
      name='DepArrSubscriptionMarketing'
      component={SubscriptionMarketing}
      subscriptionType='DEPARR'
      allowSitemap
      allowRobots
      exact
    />
    {/* This route MUST be above 'flight-details/:carrier/:flightNumber' -@ma */}
    <RouteWrapper
      onEnter={['validateTrackerDate']}
      path='/flight-details/:carrier/:flightNumber/:year/:month/:day/:flightId?'
      component={ExtendedDetails}
      name='ExtendedDetails'
    />
    <RouteWrapper
      path='/flight-details/:carrier/:flightNumber/:flightId'
      component={ExtendedDetails}
      name='ExtendedDetails'
    />
    <Route
      path='/flight-details/:carrier/:flightNumber'
      exact
      allowSitemap
      render={(renderProps) => {
        const { params } = renderProps.match;
        const now = moment();
        return (
          <Redirect
            {...renderProps}
            to={`/flight-details/${params.carrier}/${params.flightNumber}/${now.format('YYYY')}/${now.format('MM')}/${now.format('D')}`}
          />
        );
      }}
    />
    <Route
      path='/flight-alerts'
      exact
      disallowRobots
      render={() => (
        <Redirect
          push={false}
          to='/flight-alerts/home'
        />
      )}
    />
    <RouteWrapper
      path='/flight-alerts/home'
      onEnter={OnEnterFlightAlerts}
      component={FlightAlertsIndex}
      name='FlightAlerts'
    />
    <RouteWrapper
      path='/flight-alerts/new/:carrier/:flightNumber/:year/:month/:day/:departureAirport/:arrivalAirport/:trackerReferral?'
      onEnter={OnEnterFlightAlerts}
      component={NewFlightAlert}
      name='NewFlightAlert'
    />
    <RouteWrapper
      path='/flight-alerts/new/:carrier/:flightNumber/:year/:month/:day'
      onEnter={OnEnterFlightAlerts}
      component={SegmentSelect}
      name='FlightAlertsSegmentSelect'
      exact
    />
    <Redirect
      path='/flight-alerts/history'
      to='/flight-alerts/history/home'
      push={false}
      exact
    />
    <RouteWrapper
      path='/flight-alerts/history/home'
      onEnter={OnEnterFlightAlerts}
      component={FlightAlertHistory}
      name='FlightAlertsHistory'
    />
    <RouteWrapper
      path='/flight-alerts/history/details/:wmaId'
      onEnter={OnEnterFlightAlerts}
      component={FlightAlertHistoryDetails}
      name='FlightAlertsHistoryDetails'
    />
    <RouteWrapper
      path='/airport-delays/:region?'
      component={DelayMap}
      name='AirportDelayMap'
      allowSitemap
    />
    <RouteWrapper
      path='/flight-ontime-performance-rating/:carrierCode/:flightNumber/:departureAirport?/:arrivalAirport?'
      component={OnTimePerformance}
      name='OnTimePerformance'
      allowSitemap
    />
    <RouteWrapper
      path='/monthly-performance-reports/airlines'
      exact
      component={MonthlyPerformanceReports}
      name='MonthlyPerformanceReportsAirlines'
      allowSitemap
    />
    <RouteWrapper
      path='/monthly-performance-reports/airports'
      exact
      component={MonthlyPerformanceReports}
      name='MonthlyPerformanceReportsAirports'
      allowSitemap
    />
    <RouteWrapper
      path='/global-cancellations-and-delays'
      exact
      component={GlobalCancellationsDelays}
      name='GlobalCancellationsDelays'
      allowSitemap
    />
    <NotFound />
  </Switch>
);



// WEBPACK FOOTER //
// ./modules/App/components/routes.js